import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type Props = {
  isTrial: boolean;
  isExpired: boolean;
  expireAt?: string | null;
};

/**
 * Bannière #101 affichée si user en essai gratuit OU expiré.
 * Dismissible mais réapparaît après 1 minute (anti-cache permanent).
 */
export default function TrialBanner({ isTrial, isExpired, expireAt }: Props) {
  const { t, i18n } = useTranslation();
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!hidden) return;
    const id = setTimeout(() => setHidden(false), 60_000);
    return () => clearTimeout(id);
  }, [hidden]);

  if (!isTrial && !isExpired) return null;
  if (hidden) return null;

  let message: string;
  let bgColor: string;
  if (isExpired) {
    message = t('trial.bannerExpired');
    bgColor = '#dc3545';
  } else {
    const expireLabel = expireAt
      ? new Date(expireAt).toLocaleDateString(i18n.language === 'ar' ? 'ar-DZ' : 'fr-FR')
      : '';
    message = expireLabel
      ? t('trial.bannerTrialWithExpire', { date: expireLabel })
      : t('trial.bannerTrialNoExpire');
    bgColor = '#ff9800';
  }

  return (
    <div style={{
      background: bgColor,
      color: 'white',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      fontSize: '14px',
      flexWrap: 'wrap',
    }}>
      <i className={isExpired ? 'bi bi-exclamation-triangle-fill' : 'bi bi-info-circle-fill'}></i>
      <span>{message}</span>
      <Link
        to={`/${i18n.language}/dashboard/subscription`}
        style={{
          background: 'rgba(255,255,255,0.25)',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '4px',
          textDecoration: 'none',
          fontWeight: 600,
        }}
      >
        {t('trial.subscribe')}
      </Link>
      <button
        type="button"
        onClick={() => setHidden(true)}
        aria-label={t('common.close', 'Fermer')}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          fontSize: '18px',
          lineHeight: 1,
          padding: '0 4px',
        }}
      >
        ×
      </button>
    </div>
  );
}
